import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {Invoice} from '../types';
import {StatusBadge} from '../profile/StatusBadge';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `created` on a Stripe invoice arrives as a UNIX EPOCH IN SECONDS despite
// being typed `string` on the Invoice type. The old column coerced it inline
// (`item.created as unknown as number * 1000`) for display only; that
// conversion now lives in `createdAtMillis` on the record (see useInvoices.ts)
// so DISPLAY and SORT agree on one value.
//
// ⚠️ `amountDue` is in CENTS. The Amount column sorts on the raw cents value
// rather than the formatted "12.34 usd" string — sorting the string would put
// "100.00" before "9.00" (localeCompare on digits), and mixing currencies into
// one lexical order is worse still. `amountDue` is the honest sort key; the
// currency is shown, not sorted on.
export const useInvoicesTableColumns = () => {

    const columns: DataTableColumn<Invoice>[] = [
        {
            accessor: 'number',
            sortable: true,
            title: I18n.get('Number'),
            width: '1fr',
            // Nullable on the Stripe API — a draft invoice has no number until
            // it is finalised. Falls back to the id so the row is never a
            // blank link.
            render: (item: Invoice) => (
                <NavLink to={`/invoices/${encodeURIComponent(item.id)}`}>
                    {item.number || item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'customerName',
            sortable: true,
            title: I18n.get('Customer'),
            width: '1fr',
            // Sorts by name, falls back to the id for display. A customer
            // without a name on file still gets a working link.
            render: (item: Invoice) => (
                <NavLink to={`/customers/${encodeURIComponent(item.customerId)}`}>
                    {item.customerName || item.customerId}
                </NavLink>
            ),
        },
        {
            // Cents. See the note above on why not the formatted string.
            accessor: 'amountDue',
            sortable: true,
            title: I18n.get('Amount'),
            width: '1fr',
            render: (item: Invoice) => (
                typeof item.amountDue === 'number'
                    ? `${(item.amountDue / 100).toFixed(2)} ${(item.currency ?? '').toUpperCase()}`
                    : '—'
            ),
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '1fr',
            // Enum state renders as a badge, not a raw string (§13).
            render: (item: Invoice) => <StatusBadge status={item.status}/>,
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `created` (seconds) is still the source.
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Invoice) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
